/**
 * gateSymbolRenderer.js — floor-plan door symbol for gates ('tor', #611, Block B)
 *
 * A placed gate is a Fabric Rect (width = opening width, height = wall-band
 * thickness, centered on the structure edge, rotated to the edge angle). This
 * module replaces the rect's default fill/stroke render with the classic
 * architectural door symbol:
 *
 *   - a subtle opening band (marks where the door sits — the parent structure
 *     still renders through, non-destructive)
 *   - two jamb ticks at the opening ends
 *   - the door leaf (Türblatt) drawn in the open (90°) position
 *   - a quarter-circle swing arc from closed to open
 *
 * Two independent parameters drive the shape:
 *   hinge: 'left' | 'right' | 'double'   (pivot side; double = two half leaves)
 *   swing: 'in' | 'out'                  (which side of the structure it opens)
 *
 * The geometry is a pure function (computeGateSymbol) so the hinge × swing ×
 * double matrix is unit-testable without a canvas. Only new gates (placed by
 * the point tool, carrying data.hinge) render as a symbol — legacy standalone
 * gates keep their original rectangle look untouched.
 */

import { GATE_DEPTH_PX, isGate, getGardenTypeId, WINDOW_TYPE_ID } from './gateLinkUtils';

export const DOOR_LEAF_WIDTH = 2.5;
export const DOOR_ARC_WIDTH = 1.5;
export const DOOR_JAMB_WIDTH = 2;
export const GATE_SELECTED_COLOR = '#10B981';
export const GATE_DEFAULT_COLOR = '#1a3333';

const HALF_PI = Math.PI / 2;
const TAU = Math.PI * 2;

/** Normalize an angle to (-π, π]. */
const normalizePi = (a) => {
  let x = a;
  while (x > Math.PI) x -= TAU;
  while (x <= -Math.PI) x += TAU;
  return x;
};

/**
 * Map the legacy `oeffnungsrichtung` property (old standalone gates) onto the
 * new `hinge` parameter. links → left, rechts → right, beide → double.
 */
export const legacyHingeFromOeffnungsrichtung = (value) => {
  switch (value) {
    case 'links':
      return 'left';
    case 'rechts':
      return 'right';
    case 'beide':
      return 'double';
    default:
      return null;
  }
};

/**
 * Compute the door-symbol geometry in the gate's local coordinate system.
 * Local origin = gate center; the opening runs along x from -w/2 to +w/2 with
 * the structure edge at y = 0. swing 'out' opens toward +y, 'in' toward -y.
 *
 * @param {object} params
 * @param {number} params.width - opening width in local px
 * @param {number} [params.height] - wall-band thickness (jamb length)
 * @param {'left'|'right'|'double'} [params.hinge]
 * @param {'in'|'out'} [params.swing]
 * @returns {{ width, height, swingSign, jambs, leaves }}
 *   leaves: [{ pivot, tip, arc: { cx, cy, radius, startAngle, endAngle, anticlockwise } }]
 */
export function computeGateSymbol({ width, height = GATE_DEPTH_PX, hinge = 'left', swing = 'in' }) {
  const w = Math.max(0, width || 0);
  const halfW = w / 2;
  const swingSign = swing === 'out' ? 1 : -1;
  const openAngle = swingSign * HALF_PI;

  const makeLeaf = (pivotX, closedAngle, radius) => {
    const signedDiff = normalizePi(openAngle - closedAngle);
    return {
      pivot: { x: pivotX, y: 0 },
      tip: {
        x: pivotX + radius * Math.cos(openAngle),
        y: radius * Math.sin(openAngle),
      },
      arc: {
        cx: pivotX,
        cy: 0,
        radius,
        startAngle: closedAngle,
        endAngle: closedAngle + signedDiff, // exactly ±90° → always the minor arc
        anticlockwise: signedDiff < 0,
      },
    };
  };

  let leaves;
  if (hinge === 'double') {
    leaves = [
      makeLeaf(-halfW, 0, halfW), // left leaf swings from the +x (closed) direction
      makeLeaf(halfW, Math.PI, halfW), // right leaf swings from the -x direction
    ];
  } else if (hinge === 'right') {
    leaves = [makeLeaf(halfW, Math.PI, w)];
  } else {
    leaves = [makeLeaf(-halfW, 0, w)];
  }

  const jambLen = Math.max(height, 10);
  const jambs = [-halfW, halfW].map((x) => ({
    x1: x,
    y1: -jambLen / 2,
    x2: x,
    y2: jambLen / 2,
  }));

  return { width: w, height, swingSign, jambs, leaves };
}

/**
 * Draw a computed symbol onto a 2D context already transformed into the gate's
 * local space (Fabric calls _render with such a context).
 */
export function drawGateSymbol(ctx, symbol, { lineColor, fillColor }) {
  const { width, height, jambs, leaves } = symbol;
  const halfW = width / 2;
  const halfH = Math.max(height, 10) / 2;

  ctx.save();

  // Subtle opening band (marks the doorway; structure renders through it)
  ctx.fillStyle = fillColor;
  ctx.fillRect(-halfW, -halfH, width, halfH * 2);

  ctx.lineCap = 'round';
  ctx.strokeStyle = lineColor;

  // Jamb ticks
  ctx.lineWidth = DOOR_JAMB_WIDTH;
  jambs.forEach((j) => {
    ctx.beginPath();
    ctx.moveTo(j.x1, j.y1);
    ctx.lineTo(j.x2, j.y2);
    ctx.stroke();
  });

  leaves.forEach((leaf) => {
    // Swing arc
    ctx.lineWidth = DOOR_ARC_WIDTH;
    ctx.beginPath();
    ctx.arc(
      leaf.arc.cx,
      leaf.arc.cy,
      leaf.arc.radius,
      leaf.arc.startAngle,
      leaf.arc.endAngle,
      leaf.arc.anticlockwise
    );
    ctx.stroke();

    // Door leaf in the open position
    ctx.lineWidth = DOOR_LEAF_WIDTH;
    ctx.beginPath();
    ctx.moveTo(leaf.pivot.x, leaf.pivot.y);
    ctx.lineTo(leaf.tip.x, leaf.tip.y);
    ctx.stroke();
  });

  ctx.restore();
}

/**
 * Resolve hinge/swing for a gate object, honoring the new data fields and
 * falling back to the legacy oeffnungsrichtung mapping.
 */
export function resolveGateSymbolParams(gate) {
  const data = gate?.data || {};
  const hinge =
    data.hinge || legacyHingeFromOeffnungsrichtung(data.oeffnungsrichtung) || 'left';
  const swing = data.swing || 'in';
  return { hinge, swing };
}

/**
 * Whether a gate should render as the floor-plan symbol. Only gates carrying
 * the new `hinge` field qualify — legacy standalone gates keep their original
 * rectangle rendering untouched (regression-safe, #611 Block A checklist).
 */
export const usesGateSymbol = (gate) => isGate(gate) && !!gate.data?.hinge;

/**
 * Install the door-symbol renderer on a gate by overriding its _render.
 * Disables object caching so the swing arc (which extends beyond the rect's
 * own bounding box) is not clipped by an offscreen cache. Idempotent.
 */
export function applyGateSymbolRendering(gate) {
  if (!gate) return;
  gate.set({ objectCaching: false });
  gate._gateSymbolRender = true;
  gate._render = function _renderGateSymbol(ctx) {
    const { hinge, swing } = resolveGateSymbolParams(this);
    const symbol = computeGateSymbol({
      width: this.width,
      height: this.height,
      hinge,
      swing,
    });
    const isActive = this.canvas?.getActiveObject?.() === this;
    const lineColor = isActive
      ? GATE_SELECTED_COLOR
      : this.data?.color || GATE_DEFAULT_COLOR;
    const fillColor = isActive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(31, 79, 79, 0.10)';
    drawGateSymbol(ctx, symbol, { lineColor, fillColor });
  };
}

/**
 * Re-install the symbol renderer on a loaded gate (called during canvas load,
 * mirroring restoreFencePostRendering). No-op for legacy gates.
 */
export function restoreGateSymbolRendering(gate) {
  if (usesGateSymbol(gate)) {
    applyGateSymbolRendering(gate);
  }
}

// ---------------------------------------------------------------------------
// Window symbol (#690) — floor-plan glazing mark
//
// A window shares the gate's placement/link mechanic but is far simpler in the
// plan view: no hinge, no swing arc. It reads as a slim double stroke across
// the opening (the two glazing lines) between two jamb ticks. Rendered in the
// gate's local space just like the door symbol.
// ---------------------------------------------------------------------------

/** Fraction of the wall-band half-thickness at which the two glazing lines sit. */
const WINDOW_PANE_OFFSET_RATIO = 0.42;
export const WINDOW_PANE_WIDTH = 1.5;
export const WINDOW_JAMB_WIDTH = 2;

/**
 * Compute the window-symbol geometry in the window's local coordinate system.
 * Local origin = center; the opening runs along x from -w/2 to +w/2, the wall
 * band along y with thickness `height`.
 *
 * @returns {{ width, height, jambs, panes }}
 *   jambs: two end ticks [{x1,y1,x2,y2}]
 *   panes: two glazing lines across the opening [{x1,y1,x2,y2}]
 */
export function computeWindowSymbol({ width, height = GATE_DEPTH_PX }) {
  const w = Math.max(0, width || 0);
  const halfW = w / 2;
  const jambLen = Math.max(height, 10);
  const halfH = jambLen / 2;
  const paneOffset = halfH * WINDOW_PANE_OFFSET_RATIO;

  const jambs = [-halfW, halfW].map((x) => ({
    x1: x,
    y1: -halfH,
    x2: x,
    y2: halfH,
  }));

  const panes = [-paneOffset, paneOffset].map((y) => ({
    x1: -halfW,
    y1: y,
    x2: halfW,
    y2: y,
  }));

  return { width: w, height, jambs, panes };
}

/**
 * Draw a computed window symbol onto a context already transformed into the
 * window's local space.
 */
export function drawWindowSymbol(ctx, symbol, { lineColor, fillColor }) {
  const { width, height, jambs, panes } = symbol;
  const halfW = width / 2;
  const halfH = Math.max(height, 10) / 2;

  ctx.save();

  // Subtle opening band (structure renders through it, non-destructive)
  ctx.fillStyle = fillColor;
  ctx.fillRect(-halfW, -halfH, width, halfH * 2);

  ctx.lineCap = 'round';
  ctx.strokeStyle = lineColor;

  // Jamb ticks at the two ends
  ctx.lineWidth = WINDOW_JAMB_WIDTH;
  jambs.forEach((j) => {
    ctx.beginPath();
    ctx.moveTo(j.x1, j.y1);
    ctx.lineTo(j.x2, j.y2);
    ctx.stroke();
  });

  // Two glazing lines across the opening
  ctx.lineWidth = WINDOW_PANE_WIDTH;
  panes.forEach((p) => {
    ctx.beginPath();
    ctx.moveTo(p.x1, p.y1);
    ctx.lineTo(p.x2, p.y2);
    ctx.stroke();
  });

  ctx.restore();
}

/** Whether an object is a window that should render as the glazing symbol. */
export const usesWindowSymbol = (obj) => getGardenTypeId(obj) === WINDOW_TYPE_ID;

/**
 * Install the window-symbol renderer on a window by overriding its _render.
 * Idempotent; disables object caching (parallels applyGateSymbolRendering).
 */
export function applyWindowSymbolRendering(win) {
  if (!win) return;
  win.set({ objectCaching: false });
  win._windowSymbolRender = true;
  win._render = function _renderWindowSymbol(ctx) {
    const symbol = computeWindowSymbol({ width: this.width, height: this.height });
    const isActive = this.canvas?.getActiveObject?.() === this;
    const lineColor = isActive
      ? GATE_SELECTED_COLOR
      : this.data?.color || '#5B7C8A';
    const fillColor = isActive ? 'rgba(16, 185, 129, 0.12)' : 'rgba(91, 124, 138, 0.12)';
    drawWindowSymbol(ctx, symbol, { lineColor, fillColor });
  };
}

/** Whether an object carries either opening symbol (gate OR window, #690). */
export const usesOpeningSymbol = (obj) => usesGateSymbol(obj) || usesWindowSymbol(obj);

/**
 * Re-install the correct opening-symbol renderer on a loaded object (canvas
 * load). Dispatches by kind: door symbol for gates, glazing symbol for windows.
 */
export function restoreOpeningSymbolRendering(obj) {
  if (usesGateSymbol(obj)) {
    applyGateSymbolRendering(obj);
  } else if (usesWindowSymbol(obj)) {
    applyWindowSymbolRendering(obj);
  }
}
